'use client';

import { useEffect, useRef, useState } from 'react';
import Script from 'next/script';

// Service price map (in INR — adjust as needed)
const SERVICE_PRICES: Record<string, number> = {
  'signature-gel': 65,
  'bespoke-art': 95,
  'luxury-set': 130,
  bridal: 200,
  consultation: 0,
};

const SERVICE_LABELS: Record<string, string> = {
  'signature-gel': 'Signature Gel Manicure — ₹65',
  'bespoke-art': 'Bespoke Nail Art — From ₹95',
  'luxury-set': 'Luxury Full Set — From ₹130',
  bridal: 'Bridal Package — ₹200',
  consultation: 'Free Consultation',
};

export default function BookingSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    date: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'loading' | 'success' | 'error'>(
    'idle'
  );
  const [paymentError, setPaymentError] = useState('');
  const [scriptLoaded, setScriptLoaded] = useState(false);

  useEffect(() => {
    const items = sectionRef.current?.querySelectorAll('.reveal');
    if (!items) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.remove('hidden-start');
        });
      },
      { threshold: 0.1 }
    );
    items.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const selectedPrice = formData.service ? SERVICE_PRICES[formData.service] ?? 0 : 0;
  const isPaid = formData.service && selectedPrice > 0;

  const handlePayment = async () => {
    if (!scriptLoaded || paymentStatus === 'loading') return;
    setPaymentStatus('loading');
    setPaymentError('');

    try {
      const orderRes = await fetch('/api/razorpay/order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: selectedPrice,
          currency: 'INR',
          receipt: `booking_${formData.service}_${Date.now()}`,
        }),
      });

      const orderData = await orderRes.json();
      if (!orderData.success) throw new Error(orderData.error || 'Failed to create order');

      const options = {
        key: orderData.keyId,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'Luxury Nail Art',
        description: SERVICE_LABELS[formData.service] || 'Nail Art Service',
        order_id: orderData.orderId,
        prefill: {
          name: formData.name,
          email: formData.email,
          contact: formData.phone,
        },
        notes: {
          service: formData.service,
          date: formData.date,
          message: formData.message,
        },
        theme: { color: '#c9a96e' },
        handler: async (response: {
          razorpay_order_id: string;
          razorpay_payment_id: string;
          razorpay_signature: string;
        }) => {
          const verifyRes = await fetch('/api/razorpay/verify', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(response),
          });
          const verifyData = await verifyRes.json();

          if (verifyData.success) {
            setPaymentStatus('success');
            setSubmitted(true);
          } else {
            setPaymentStatus('error');
            setPaymentError(verifyData.error || 'Payment verification failed');
          }
        },
        modal: {
          ondismiss: () => {
            setPaymentStatus('idle');
          },
        },
      };

      const razorpay = new (window as { Razorpay: new (opts: typeof options) => { open: () => void; on: (event: string, cb: (res: { error: { description: string } }) => void) => void } }).Razorpay(options);
      razorpay.on('payment.failed', (res: { error: { description: string } }) => {
        setPaymentStatus('error');
        setPaymentError(res.error.description || 'Payment failed');
      });
      razorpay.open();
    } catch (error) {
      setPaymentStatus('error');
      setPaymentError(error instanceof Error ? error.message : 'Something went wrong');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isPaid) {
      await handlePayment();
    } else {
      // Free consultation — submit without payment
      setSubmitted(true);
    }
  };

  const inputClass =
    'w-full bg-surface border border-border rounded-2xl px-4 py-3.5 text-sm text-primary placeholder-muted focus:outline-none focus:border-accent transition-colors duration-200 font-sans';

  return (
    <section id="booking" className="py-24 bg-background relative overflow-hidden" ref={sectionRef}>
      <Script
        src="https://checkout.razorpay.com/v1/checkout.js"
        onLoad={() => setScriptLoaded(true)}
      />

      {/* Decorative blurs */}
      <div className="absolute -top-32 -right-32 w-80 h-80 bg-accent/8 rounded-full blur-3xl" />
      <div className="absolute -bottom-32 -left-32 w-64 h-64 bg-accent-rose/8 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Top CTA text */}
        <div className="text-center mb-16">
          <span className="text-xs font-mono text-muted uppercase tracking-widest block mb-3 border-b border-border w-fit pb-1 mx-auto reveal hidden-start">
            05 — Book an Appointment
          </span>
          <h2 className="font-display text-4xl md:text-6xl font-medium text-primary tracking-tight leading-tight mb-5 reveal hidden-start reveal-delay-1">
            Ready for your
            <br />
            <span className="italic text-accent-rose">next set?</span>
          </h2>
          <p className="text-muted text-base max-w-lg mx-auto leading-relaxed reveal hidden-start reveal-delay-2">
            Slots fill up fast — I'm currently booking{' '}
            <strong className="text-primary">2–3 weeks out</strong>. Send a request and I'll confirm
            availability within 24 hours.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          {/* Form */}
          <div className="bg-surface rounded-5xl p-8 md:p-10 border border-border shadow-card reveal hidden-start">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg
                    className="w-8 h-8 text-accent"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </div>
                <h3 className="font-display text-2xl font-medium text-primary mb-3">
                  {paymentStatus === 'success' ? 'Payment successful!' : 'Request received!'}
                </h3>
                <p className="text-muted text-sm leading-relaxed">
                  {paymentStatus === 'success' ?'Your booking is confirmed. Sofia will reach out within 24 hours with appointment details.' :'Thank you for reaching out. Sofia will confirm your appointment within 24 hours via email.'}
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-primary uppercase tracking-wider mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="Your full name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-primary uppercase tracking-wider mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      required
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-primary uppercase tracking-wider mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      placeholder="+1 (555) 000-0000"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-primary uppercase tracking-wider mb-2">
                      Preferred Date
                    </label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      className={inputClass}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-primary uppercase tracking-wider mb-2">
                    Service *
                  </label>
                  <select
                    required
                    value={formData.service}
                    onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                    className={`${inputClass} appearance-none cursor-pointer`}
                  >
                    <option value="" disabled>
                      Select a service...
                    </option>
                    <option value="signature-gel">Signature Gel Manicure — ₹65</option>
                    <option value="bespoke-art">Bespoke Nail Art — From ₹95</option>
                    <option value="luxury-set">Luxury Full Set — From ₹130</option>
                    <option value="bridal">Bridal Package — ₹200</option>
                    <option value="consultation">Free Consultation</option>
                  </select>
                </div>

                {/* Price summary */}
                {formData.service && (
                  <div className="bg-accent/5 border border-accent/20 rounded-2xl px-4 py-3 flex items-center justify-between">
                    <span className="text-sm text-muted">
                      {isPaid ? 'Amount due now' : 'No payment required'}
                    </span>
                    <span className="text-sm font-semibold text-primary">
                      {isPaid ? `₹${selectedPrice}` : 'Free'}
                    </span>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-primary uppercase tracking-wider mb-2">
                    Design Inspiration / Notes
                  </label>
                  <textarea
                    rows={4}
                    placeholder="Tell me about your vision — colors, styles, references, occasion..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className={`${inputClass} resize-none`}
                  />
                </div>

                {paymentStatus === 'error' && paymentError && (
                  <div className="bg-red-50 border border-red-200 rounded-2xl px-4 py-3">
                    <p className="text-sm text-red-600">{paymentError}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={paymentStatus === 'loading'}
                  className="w-full py-4 bg-primary text-white text-sm font-semibold rounded-full hover:bg-foreground-soft transition-all duration-300 flex items-center justify-center gap-2 shadow-card disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {paymentStatus === 'loading' ? (
                    <>
                      <svg
                        className="w-4 h-4 animate-spin"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48 2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48 2.83-2.83" />
                      </svg>
                      Processing...
                    </>
                  ) : isPaid ? (
                    <>
                      Pay ₹{selectedPrice} & Book
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M5 12h14m-7-7 7 7-7 7" />
                      </svg>
                    </>
                  ) : (
                    <>
                      Send Booking Request
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M5 12h14m-7-7 7 7-7 7" />
                      </svg>
                    </>
                  )}
                </button>

                <p className="text-xs text-muted text-center">
                  {isPaid
                    ? 'Secure payment powered by Razorpay. Test card: 4100 2800 0000 1007' :'No payment required for free consultation. Sofia will confirm within 24 hours.'}
                </p>
              </form>
            )}
          </div>

          {/* Info panel */}
          <div className="flex flex-col gap-6 reveal hidden-start reveal-delay-2">
            {/* Studio info */}
            <div className="bg-cream-dark rounded-4xl p-7 border border-border">
              <h3 className="font-display text-xl font-medium text-primary mb-5">Studio Details</h3>
              <div className="space-y-4">
                {[
                  {
                    icon: '📍',
                    label: 'Location',
                    value:
                      'West Village, Manhattan\nNYC (exact address sent on booking confirmation)',
                  },
                  { icon: '🕐', label: 'Hours', value: 'Tuesday – Saturday\n10:00 AM – 7:00 PM' },
                  {
                    icon: '📱',
                    label: 'Quick Contact',
                    value: '+1 (212) 555-0187\nhello@luxurynailart.com',
                  },
                  {
                    icon: '⏱️',
                    label: 'Response Time',
                    value: 'Within 24 hours\nSame-day available for urgent requests (+$20)',
                  },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="text-xl flex-shrink-0">{item.icon}</span>
                    <div>
                      <p className="text-xs font-semibold text-muted uppercase tracking-wider mb-0.5">
                        {item.label}
                      </p>
                      <p className="text-sm text-primary whitespace-pre-line leading-relaxed">
                        {item.value}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Cancellation policy */}
            <div className="bg-surface rounded-4xl p-7 border border-border">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-accent/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-4 h-4 text-accent"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 8v4m0 4h.01" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-primary mb-1">Cancellation Policy</p>
                  <p className="text-sm text-muted leading-relaxed">
                    Free cancellation up to 48 hours before your appointment. Late cancellations may
                    incur a 50% service fee.
                  </p>
                </div>
              </div>
            </div>

            {/* Social proof strip */}
            <div className="bg-primary rounded-4xl p-7 text-white">
              <p className="font-display text-lg italic text-white/80 mb-4">
                "Booked within 10 minutes of finding her Instagram. No regrets — she's the best in
                the city."
              </p>
              <div className="flex items-center gap-3">
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <svg
                      key={i}
                      className="w-3.5 h-3.5 fill-accent text-accent"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  ))}
                </div>
                <span className="text-white/60 text-xs font-mono uppercase tracking-widest">
                  — Recent Google Review
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
